import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Select from "react-select";
import Collapse from "react-bootstrap/Collapse";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import { customEmailSend, deleteUser, getUsers } from "../../../services/UserService";
import { Modal } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import CustomClassicEditor from "../CustomClassicEditor";

const User = () => {
  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);
  const hasFetched = useRef(false);
  const location = useLocation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);

  const [emailData, setEmailData] = useState({
    subject: "",
    message: "",
    extra: "",
    sendToAll: false,
    type: "html",
    cta_button: false,
    appUrl: "",
  });

  const cleanEditorContent = (html = "") => {
    if (!html) return "";

    let cleaned = html.trim();

    // loop because last may be [empty_line] + <p>&nbsp;</p> + [empty_line]
    let changed = true;

    while (changed) {
      changed = false;

      // remove only one trailing empty paragraph block
      const removedParagraph = cleaned.replace(
        /<p>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*$/i,
        ""
      );

      if (removedParagraph !== cleaned) {
        cleaned = removedParagraph.trim();
        changed = true;
      }

      // remove only one trailing [empty_line]
      const removedToken = cleaned.replace(
        /\[empty_line\]\s*$/i,
        ""
      );

      if (removedToken !== cleaned) {
        cleaned = removedToken.trim();
        changed = true;
      }
    }

    return cleaned;
  };

  const handleSelectUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((userId) => userId !== id)
        : [...prev, id]
    );
  };

  // Select all users
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      const allIds = users.map((u) => u.id);
      setSelectedUsers(allIds);
    }
    setSelectAll(!selectAll);
  };

  const fetchUsers = async (pageNumber = 1, query = "") => {
    try {
      setLoading(true);
      const response = await getUsers(pageNumber, limit, query);
      const { data, pagination } = response.data;
      setUsers(data || []);
      setTotalPages(pagination?.totalPages || 0);
      setTotalItems(pagination?.totalItems || 0);
      setPage(pagination?.page || 1);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      const stateData = location.state;

      if (stateData) {
        setSearchTerm(stateData.searchTerm || "");
        fetchUsers(stateData.page || 1, stateData.searchTerm || "");
      } else {
        fetchUsers(1);
        hasFetched.current = true;
      }
    }
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchUsers(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setPage(1);
    fetchUsers(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
      fetchUsers(newPage, searchTerm);
    }
  };

  const handleDeleteUser = (userId, status) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once status changed, you will not be able to log in!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: `${status === "active" ? "#dd6b55" : "#4caf50"}`,
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, change it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Call your delete API here
          await deleteUser(userId); // make sure you have a deleteUser function
          Swal.fire("Success!", "User status has been changed.", "success");
          // Refresh the list
          fetchUsers(page, searchTerm);
        } catch (error) {
          Swal.fire("Error!", "Failed to change user status.", "error");
        }
      }
    });
  };

  const handleSendEmail = async () => {
    try {
      if (!emailData.subject || !emailData.message) {
        Swal.fire("Subject and message are required");
        return;
      }

      if (!emailData.sendToAll && selectedUsers.length === 0) {
        Swal.fire("Please select users or choose send to all");
        return;
      }

      let payload = {
        subject: emailData.subject,
        message: cleanEditorContent(emailData.message),
        type: emailData.type,
        cta_button: emailData.cta_button,
        appUrl: emailData.appUrl,
      };

      if (emailData.sendToAll) {
        payload.sendToUsers = true;
        payload.sendToSubscribers = true;
      } else {
        payload.userIds = selectedUsers;
      }

      console.log("Payload:", payload);

      await customEmailSend(payload);

      Swal.fire("Success", "Email sent successfully", "success");
      setShowEmailModal(false);
      setEmailData({
        subject: "",
        message: "",
        extra: "",
        sendToAll: false,
        type: "html",
        cta_button: false,
        appUrl: "",
      });

    } catch (error) {
      Swal.fire("Error", "Failed to send email", "error");
    }
  };

  const handleBulkAction = () => {
    if (selectedUsers.length === 0) {
      Swal.fire("Please select at least one user");
      return;
    }
    setShowEmailModal(true);
  };

  return (
    <>
      <PageTitle
        activeMenu="User"
        motherMenu="User"
        activeMenuLink={"user"}
        motherMenuLink={"user"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="filter cm-content-box box-primary">
            <div className={`content-title`} onClick={() => setOpen(!open)}>
              <div className="cpa">
                <i className="fas fa-filter me-2"></i>Filter
              </div>
              <div className="tools">
                <Link
                  to={"#"}
                  className={`SlideToolHeader ${open ? "collapse" : "expand"}`}
                >
                  <i className="fas fa-angle-up"></i>
                </Link>
              </div>
            </div>
            <Collapse in={open}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <div className="row">
                    <div className="col-xl-3 col-sm-6">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control mb-3 mb-xl-0"
                        id="exampleFormControlInput1"
                        placeholder="Name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    {/* <div className="col-xl-3 col-sm-6">
                      <label className="form-label">Date</label>
                      <div className="input-hasicon mb-sm-0 mb-3">
                        <input
                          type="date"
                          name="datepicker"
                          className=" form-control mb-xl-0 mb-3"
                        />
                      </div>
                    </div> */}
                    <div className="col-xl-9 col-sm-6 align-self-end">
                      <button
                        className="btn btn-primary me-2 rounded-sm"
                        title="Click here to Search"
                        type="button"
                        onClick={handleSearch}
                      >
                        <i className="fa fa-search me-1"></i>Filter
                      </button>
                      <button
                        className="btn btn-danger light rounded-sm me-2"
                        title="Click here to remove filter"
                        type="button"
                        onClick={handleReset}
                      >
                        <i className="fa fa-undo me-1"></i>
                        Remove Filter
                      </button>
                      {/* <button
                        className="btn btn-danger rounded-sm"
                        title="Click here to upload subscribe user data (excel/csv)"
                        type="button"
                        onClick={handleBulkAction}
                      >
                        <i className="fa fa-paper-plane me-2"></i>
                        Send Mail
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
          <div className="filter cm-content-box box-primary">
            <div className={`content-title`} onClick={() => setOpen2(!open2)}>
              <div className="cpa">
                <i className="fas fa-user me-1" />
                User List
              </div>
              <div className="tools">
                <Link
                  to={"#"}
                  className={`SlideToolHeader ${open2 ? "collapse" : "expand"}`}
                >
                  <i className="fas fa-angle-up"></i>
                </Link>
              </div>
            </div>
            <Collapse in={open2}>
              <div className="cm-content-body form excerpt">
                <div className="card-body py-3">
                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <div className="table-responsive order-list-table">
                      <div
                        id="blog_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <table className="table table-responsive-lg table-condensed flip-content">
                          <thead>
                            <tr>
                              {/* <th>
                                <input
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                />
                              </th> */}
                              <th className="text-black">S.No</th>
                              <th className="text-black">Name</th>
                              <th className="text-black">Email</th>
                              <th className="text-black">Mobile Number</th>
                              <th className="text-black">Place</th>
                              <th className="text-black">Status</th>
                              <th className="text-black">Joined At</th>
                              <th className="text-black text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.length > 0 ? (
                              users.map((item, ind) => (
                                <tr key={ind}>
                                  {/* <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.includes(item.id)}
                                      onChange={() => handleSelectUser(item.id)}
                                    />
                                  </td> */}
                                  <td>{(page - 1) * limit + ind + 1}</td>
                                  <td className="truncate">{item.name}</td>
                                  <td className="truncate">{item.email}</td>
                                  <td className="truncate">{item.mobile}</td>
                                  <td className="truncate">{item.place}</td>
                                  <td>
                                    <span
                                      className={`badge d-inline-block text-capitalize ${item.status === "active"
                                        ? "bg-success"
                                        : item.status === "inactive"
                                          ? "bg-secondary"
                                          : "bg-warning"
                                        }`}
                                    >
                                      {item.status || "Unknown"}
                                    </span>
                                  </td>
                                  <td>
                                    {new Date(
                                      item.createdAt
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </td>
                                  <td className="text-end">
                                    <Link
                                      to={`/view-user/${item.id}`}
                                      state={{ page, searchTerm }}
                                      className="btn btn-primary btn-sm content-icon me-1"
                                    >
                                      <i className="fa fa-eye"></i>
                                    </Link>
                                    <Link
                                      to={"#"}
                                      className={`${item.status === "inactive"
                                        ? "btn btn-success btn-sm content-icon ms-1"
                                        : "btn btn-danger btn-sm content-icon ms-1"
                                        }`}
                                      onClick={() =>
                                        handleDeleteUser(item.id, item.status)
                                      }
                                    >
                                      <i
                                        className={`${item.status === "inactive"
                                          ? "fa-solid fa-plus"
                                          : "fa-solid fa-minus"
                                          }`}
                                      />
                                    </Link>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center">
                                  No users found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {totalPages > 0 && (
                          <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                            <div className="dataTables_info">
                              {totalItems > 0 && (
                                <>
                                  Showing {(page - 1) * limit + 1} to{" "}
                                  {Math.min(page * limit, totalItems)} of{" "}
                                  {totalItems} entries
                                </>
                              )}
                            </div>

                            <div
                              className="dataTables_paginate paging_simple_numbers"
                              id="example2_paginate"
                            >
                              {/* Prev button */}
                              <Link
                                className={`paginate_button previous ${page === 1 ? "disabled" : ""
                                  }`}
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (page > 1) handlePageChange(page - 1);
                                }}
                              >
                                <i className="fa fa-angle-double-left" />
                              </Link>

                              <span>
                                {Array.from(
                                  { length: totalPages },
                                  (_, i) => i + 1
                                )
                                  .filter((i) => {
                                    // Always show first 2 and last 2
                                    if (i <= 2 || i > totalPages - 2)
                                      return true;
                                    // Show nearby pages of the current one
                                    if (Math.abs(page - i) <= 1) return true;
                                    return false;
                                  })
                                  .reduce((acc, i, index, arr) => {
                                    // Add ellipsis between distant page numbers
                                    if (index > 0 && i - arr[index - 1] > 1)
                                      acc.push("ellipsis");
                                    acc.push(i);
                                    return acc;
                                  }, [])
                                  .map((item, index) =>
                                    item === "ellipsis" ? (
                                      <span
                                        key={index}
                                        className="paginate_button disabled"
                                      >
                                        ...
                                      </span>
                                    ) : (
                                      <Link
                                        key={index}
                                        to="#"
                                        className={`paginate_button ${page == item ? "current" : ""
                                          }`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handlePageChange(item);
                                        }}
                                      >
                                        {item}
                                      </Link>
                                    )
                                  )}
                              </span>

                              {/* Next button */}
                              <Link
                                className={`paginate_button next ${page === totalPages ? "disabled" : ""
                                  }`}
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (page < totalPages) handlePageChange(page + 1);
                                }}
                              >
                                <i className="fa fa-angle-double-right" />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </div>
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered fullscreen="lg-down" size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Send Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Tabs */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${emailData.type === "html" ? "active" : ""}`}
                onClick={() =>
                  setEmailData({ ...emailData, type: "html" })
                }
              >
                HTML
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${emailData.type === "text" ? "active" : ""}`}
                onClick={() =>
                  setEmailData({ ...emailData, type: "text" })
                }
              >
                Text
              </button>
            </li>
          </ul>

          {/* Send to all */}
          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              checked={emailData.sendToAll}
              onChange={(e) =>
                setEmailData({
                  ...emailData,
                  sendToAll: e.target.checked,
                })
              }
            />
            <label className="form-check-label">
              Send to All Users & Subscribers
            </label>
          </div>

          {!emailData.sendToAll && (
            <p className="text-muted">
              Selected Users: {selectedUsers.length}
            </p>
          )}

          {/* Subject */}
          <input
            className="form-control mb-3"
            placeholder="Subject"
            value={emailData.subject}
            onChange={(e) =>
              setEmailData({
                ...emailData,
                subject: e.target.value,
              })
            }
          />

          {emailData.type === "html" ? (<CKEditor
            editor={CustomClassicEditor}
            data={(emailData.message).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
            onChange={(event, editor) => {
              setEmailData({
                ...emailData,
                message: editor.getData(),
              })
            }}
          />) : (
            <textarea
              className="form-control mb-3"
              rows={4}
              placeholder={
                emailData.type === "html"
                  ? "Enter HTML content"
                  : "Enter plain text"
              }
              value={emailData.message}
              onChange={(e) =>
                setEmailData({
                  ...emailData,
                  message: e.target.value,
                })
              }
            />
          )}

          {emailData.type === "html" && <CKEditor
            editor={CustomClassicEditor}
            data={(emailData.extra).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
            onChange={(event, editor) => {
              setEmailData({
                ...emailData,
                extra: editor.getData(),
              })
            }}
          />}

          {/* CTA */}
          {emailData.type === "html" && (
            <>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={emailData.cta_button}
                  onChange={(e) =>
                    setEmailData({
                      ...emailData,
                      cta_button: e.target.checked,
                    })
                  }
                />
                <label className="form-check-label">
                  Include CTA Button
                </label>
              </div>

              {emailData.cta_button && (
                <input
                  className="form-control"
                  placeholder="Enter CTA URL"
                  value={emailData.appUrl}
                  onChange={(e) =>
                    setEmailData({
                      ...emailData,
                      appUrl: e.target.value,
                    })
                  }
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowEmailModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSendEmail}
          >
            Send Email
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default User;
